import { useTranslation } from "react-i18next";
import React, {useEffect, useMemo, useState, useTransition} from "react";
import OverviewChartTab from "../chart/overview-tab/OverviewChartTab";
import OverviewTable from "../table/overview-table";
import {BarStackedChartProps, DualChart, PieChart} from "../../../../../types/static-report";
import {debounce} from "lodash";
import {staticReportStore} from "../../../../../stores/stores";
import dayjs from "dayjs";
import {
  getListCourseByUniversitySponsor,
  getListStudentByIndustryGroup,
  getListSynthesizeDataUniversityOverview,
  getListTeacherByIndustryGroupOverview,
  getListUnitResources,
  getListUnitResourcesTable,
  getUniversityVisitors,
  getUniversityVisits,
  getVisitsAndVisitorsByRegisterUniversity,
  getVisitsAndVisitorsUniversity,
} from "../../../../../service/statistical-report";
import {renderColBarStackChart, renderColDualChart} from "../../../../../utils/staticReport";

type Props = {
  dataType: string;
  universityId?: number;
};

const OverviewTab = (props: Props) => {
  const { t } = useTranslation();
  const {dataType, universityId} = props;
  const {staticReportType, filterValues, tab} = staticReportStore();
  const [isPending, startTransition] = useTransition();

  const [chartData, setChartData] = useState({
    visitsAndVisitorsUniversity: [] as DualChart[],
    visitsAndVisitorsUniversityTotal: 0,
    visitsAndVisitorsByRegisterUniversity: [] as PieChart[],
    visitsAndVisitorsByRegisterUniversityTotal: 0,
    synthesizeUniversity: [] as Array<any>,
    synthesizeUniversityTotal: 0,
    universityVisits: [] as Array<any>,
    universityVisitsTotal: 0,
    universityVisitors: [] as Array<any>,
    universityVisitorsTotal: 0,
    teacherByUniversity: [] as BarStackedChartProps[],
    teacherByUniversityTotal: 0,
    studentByUniversity: [] as BarStackedChartProps[],
    studentByUniversityTotal: 0,
    courseByUniversitySponsor: [] as BarStackedChartProps[],
    courseByUniversitySponsorTotal: 0,
    unitResources: [] as Array<any>,
    unitResourcesTotal: 0,
    unitResourcesTable: [] as Array<any>,
  });

  const [loading, setLoading] = useState({
    visitsAndVisitors: false,
    visitsAndVisitorsByRegister: false,
    teacherByUniversity: false,
    studentByUniversity: false,
    courseByUniversitySponsor: false,
    unitResources: false,
    unitResourcesTable: false,
  });

  const fetchData = async () => {
    const payload = {
      timeUnit: filterValues.timeUnit,
      from: dayjs(filterValues.dateFrom).toISOString(),
      to: dayjs(filterValues.dateTo).toISOString(),
      courseLevelIds: filterValues?.courseLevelIds,
      industryGroupIds: filterValues?.industryGroupIds,
      lecturerIds: filterValues?.lecturerIds,
      universityIds: filterValues?.universityIds,
    };
    try {
      const apiCallList = [
        {
          key: "synthesizeUniversity",
          total: "synthesizeUniversityTotal",
          api: getListSynthesizeDataUniversityOverview,
          params: payload,
        },
        {
          key: "universityVisits",
          total: "universityVisitsTotal",
          api: getUniversityVisits,
          params: payload,
        },
        {
          key: "universityVisitors",
          total: "universityVisitorsTotal",
          api: getUniversityVisitors,
          params: payload,
        },
        {
          key: "visitsAndVisitorsUniversity",
          total: "visitsAndVisitorsUniversityTotal",
          api: getVisitsAndVisitorsUniversity,
          params: payload,
        },
        {
          key: "visitsAndVisitorsByRegisterUniversity",
          total: "visitsAndVisitorsByRegisterUniversityTotal",
          api: getVisitsAndVisitorsByRegisterUniversity,
          params: payload,
        },
        {
          key: "teacherByUniversity",
          total: "teacherByUniversityTotal",
          api: getListTeacherByIndustryGroupOverview,
          params: payload,
        },
        {
          key: "studentByUniversity",
          total: "studentByUniversityTotal",
          api: getListStudentByIndustryGroup,
          params: payload,
        },
        {
          key: "courseByUniversitySponsor",
          total: "courseByUniversitySponsorTotal",
          api: getListCourseByUniversitySponsor,
          params: payload,
        },
        {
          key: "unitResources",
          total: "unitResourcesTotal",
          api: getListUnitResources,
          params: payload,
        }, {
          key: "unitResourcesTable",
          total: "unitResourcesTableTotal",
          api: getListUnitResourcesTable,
          params: payload,
        },
      ]
      await Promise.allSettled(
        apiCallList.map(async ({key, api, total, params}) => {
          setLoading((prev) => ({...prev, [key]: true, [total]: true}));
          try {
            const response = await api(params);
            startTransition(() => {
              setChartData((prevState) => ({
                ...prevState,
                [key]: response?.data?.data?.statistic || response?.data?.data,
                [total]: response?.data?.total || response?.data?.data?.total,
              }));
            })
          } catch (error) {
            console.error(`Error fetching data for ${key}:`, error);
            startTransition(() => {
              setChartData(prevState => ({
                ...prevState,
                [key]: [], // Provide fallback empty data
              }));
            });
          } finally {
            setLoading((prev) => ({...prev, [key]: false}));
          }
        })
      );
    } catch (err) {
      console.error(err)
    }
  }

  const debouncedFetchData = useMemo(
    () => debounce(fetchData, 300),
    [filterValues]
  );

  useEffect(() => {
    if (tab === "1" && universityId && universityId !== 0) {
      debouncedFetchData();
    }
    return () => {
      debouncedFetchData.cancel();
    };
  }, [filterValues, tab, universityId]);

  const memoizedChartData = useMemo(() => {
    const series = [t('Chủ trì', 'Chủ trì'), t('Phối hợp', 'Phối hợp')];
    const series1 = [t('Vãng lai', 'Vãng lai'), t('Người dùng tự do', 'Người dùng tự do'), t('Trường đăng ký', 'Trường đăng ký')];
    const series2 = [t('Học viên cũ', 'Học viên cũ'), t('Học viên đăng ký mới', 'Học viên đăng ký mới')];
    const series3 = [t('Cử nhân', 'Cử nhân'), t('Thạc sĩ', 'Thạc sĩ'), t('Phó giáo sư', 'Phó giáo sư'), t('Giáo sư', 'Giáo sư'), t('Tiến sĩ', 'Tiến sĩ'), t('Khác', 'Khác')];

    renderColDualChart(series, chartData.courseByUniversitySponsor)
    renderColDualChart(series1, chartData.universityVisits)
    renderColDualChart(series1, chartData.universityVisitors)
    renderColBarStackChart(series2, chartData.studentByUniversity)
    renderColBarStackChart(series3, chartData.teacherByUniversity)

    return {
      visitsAndVisitors: chartData.visitsAndVisitorsUniversity,
      visitsAndVisitorsByRegister: chartData.visitsAndVisitorsByRegisterUniversity,
      synthesizeUniversityList: chartData.synthesizeUniversity,
      universityVisits: chartData.universityVisits,
      universityVisitsTotal: chartData.universityVisitsTotal,
      universityVisitors: chartData.universityVisitors,
      universityVisitorsTotal: chartData.universityVisitorsTotal,
      teacherByUniversity: chartData.teacherByUniversity,
      teacherByUniversityTotal: chartData.teacherByUniversityTotal,
      studentByUniversity: chartData.studentByUniversity,
      studentByUniversityTotal: chartData.studentByUniversityTotal,
      courseByUniversitySponsor: chartData.courseByUniversitySponsor,
      courseByUniversitySponsorTotal: chartData.courseByUniversitySponsorTotal,
      unitResources: chartData.unitResources,
      unitResourcesTotal: chartData.unitResourcesTotal,
      unitResourcesTable: chartData.unitResourcesTable,
    };
  }, [chartData]);

  return (
    (universityId && universityId !== 0)
      ?
      (dataType === "1") ?
        <OverviewChartTab {...memoizedChartData} loading={loading}/> :
        <OverviewTable {...memoizedChartData} loading={isPending}/>
      :

      <p style={{fontSize: 18, color: "#5f5c5c", textAlign: "center", marginTop: "30vh"}}>{t("Không có dữ liệu", "Không có dữ liệu")}</p>
  )
};

export default OverviewTab;
